import { alpha } from '@mui/material/styles';
import {
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Checkbox,
  Button,
  Box,
  Divider,
  FormGroup,
  FormControlLabel,
  Popover,
  Paper,
  InputBase,
  Grid,
} from '@mui/material';
import PropTypes from 'prop-types';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useTranslation } from '@app/config/i18n';
import React from 'react';
import { APPLICATION_STATUS, EXPORT_TYPES } from '@app/constants';
import ApplicationsExport from "@organisms/Applications/ApplicationsExport";

export default function SubmissionsTableToolbar(props) {
  const {
    rowsSelected,
    columns,
    onToggleColumn,
    filters,
    onFilterChange,
    onSearchChange,
    opportunity,
    onDelete,
    onSendReminder,
    onSendEmail,
    onAssignReviewers,
  } = props;
  const { t } = useTranslation(['common']);
  const [searchQuery, setSearchQuery] = React.useState(filters.q || '');

  // State for column popover
  const [columnsAnchorEl, setColumnsAnchorEl] = React.useState(null);
  const isColumnsPopoverOpen = Boolean(columnsAnchorEl);

  // State for filter popover
  const [filterAnchorEl, setFilterAnchorEl] = React.useState(null);
  const isFilterPopoverOpen = Boolean(filterAnchorEl);

  // Handle column popover open/close
  const handleColumnsPopoverOpen = (event) => {
    setColumnsAnchorEl(event.currentTarget);
  };

  const handleColumnsPopoverClose = () => {
    setColumnsAnchorEl(null);
  };

  // Handle filter popover open/close
  const handleFilterPopoverOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterPopoverClose = () => {
    setFilterAnchorEl(null);
  };

  // Handle status filter change
  const handleStatusFilterChange = (status) => {
    // Toggle the status in the filter
    const currentStatusFilters = filters.status || [];
    let newStatusFilters;

    if (currentStatusFilters.includes(status)) {
      // Remove status if already selected
      newStatusFilters = currentStatusFilters.filter(s => s !== status);
    } else {
      // Add status if not selected
      newStatusFilters = [...currentStatusFilters, status];
    }

    onFilterChange('status', newStatusFilters);
  };

  // Handle registration status filter change
  const handleRegistrationStatusFilterChange = (regStatus) => {
    const currentStatusFilters = filters.registration_status || [];
    let newStatusFilters;

    if (currentStatusFilters.includes(regStatus)) {
      newStatusFilters = currentStatusFilters.filter(s => s !== regStatus);
    } else {
      newStatusFilters = [...currentStatusFilters, regStatus];
    }

    onFilterChange('registration_status', newStatusFilters);
  };

  // Handle payment status filter change
  const handlePaymentStatusFilterChange = (payStatus) => {
    const currentStatusFilters = filters.payStatus || [];
    let newStatusFilters;

    if (currentStatusFilters.includes(payStatus)) {
      newStatusFilters = currentStatusFilters.filter(s => s !== payStatus);
    } else {
      newStatusFilters = [...currentStatusFilters, payStatus];
    }

    onFilterChange('payment_status', newStatusFilters);
  };

  // Handle search input change with debouncing
  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  // Sync search query with external filters (only if different to avoid loops)
  React.useEffect(() => {
    const externalQuery = filters.q || '';
    if (searchQuery !== externalQuery) {
      setSearchQuery(externalQuery);
    }
  }, [filters.q]);

  // Debounced search effect
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onSearchChange && searchQuery !== (filters.q || '')) {
        onSearchChange(searchQuery);
      }
    }, 400); // 400ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, onSearchChange, filters.q]);

  // Clear search input
  const handleClearSearch = () => {
    setSearchQuery('');
    // Immediately trigger search change when clearing
    if (onSearchChange) {
      onSearchChange('');
    }
  };

  // Generate status options for the filter
  const statusOptions = [
    APPLICATION_STATUS.SUBMITTED,
    APPLICATION_STATUS.UNDER_REVIEW,
    APPLICATION_STATUS.REVIEWED,
    APPLICATION_STATUS.ACCEPTED,
    APPLICATION_STATUS.REJECTED
  ];

  const registrationStatusOptions = ['registered', 'unregistered'];
  const paymentStatusOptions = ['paid', 'unpaid'];

  // Label formatter for status
  const formatStatusLabel = (status) => {
    if (status === APPLICATION_STATUS.UNDER_REVIEW) return 'Under Review';
    return status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase();
  };

  // Common styles for popovers
  const popoverPaperStyle = {
    p: 2,
    width: '100%',
    maxHeight: 350,
    overflow: 'auto',
    boxShadow: 'none',
    border: 'none'
  };

  // Bulk action handlers
  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  const handleSendReminder = () => {
    if (onSendReminder) {
      onSendReminder();
    }
  };

  const handleSendEmail = () => {
    if (onSendEmail) {
      onSendEmail();
    }
  };

  const handleAssignReviewers = () => {
    if (onAssignReviewers) {
      onAssignReviewers();
    }
  };

  const exportableColumns = columns
    .filter(col => col.isVisible && col.fieldType && col.fieldType !== "Actions")
    .map(col => col.fieldType);

  const queryParams = new URLSearchParams({ columns: exportableColumns.join(","), });

  return (
    <Toolbar
      variant="dense"
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(rowsSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        backgroundColor: '#FFFFFF',
        minHeight: '64px',
        paddingTop: '8px',
        paddingBottom: '8px',
      }}
    >
      {/* Left side - selection count or search box */}
      <Box flex="1 1 100%" display="flex" alignItems="center">
        {rowsSelected > 0 ? (
          <Typography color="inherit" variant="subtitle1" component="div" sx={{ fontSize: '14px' }}>
            {rowsSelected} {t('common:selected')}
          </Typography>
        ) : (
           <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: '8px',
              border: '1px solid rgba(0, 0, 0, 0.15)',
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              px: 1.5, // horizontal padding inside box
              py: 0.5, // vertical padding
              minHeight: 40, // ensures balanced height
            }}
          >
            <InputBase
              sx={{
                flex: 1,
                fontSize: '0.95rem',
                pl: 0.5,
                '& input': {
                  paddingY: '6px', // equal top/bottom
                },
              }}
              placeholder={t('common:Search')}
              inputProps={{ 'aria-label': 'search submissions' }}
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <IconButton
              aria-label="search"
              size="small"
              sx={{
                color: 'rgba(0, 0, 0, 0.54)',
                ml: 0.5,
                p: 0.75,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              {searchQuery ? (
                <ClearIcon fontSize="small" onClick={handleClearSearch} />
              ) : (
                <SearchIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Right side - actions */}
      <Box display="flex" alignItems="center" ml="auto" gap={1}>
        {/* Export button - always visible */}
        <Tooltip title={t('common:Export')}>
          <IconButton size="medium" sx={{ color: 'rgba(0, 0, 0, 0.54)' }}>
            <ApplicationsExport opportunitySlug={opportunity?.slug} exportType={EXPORT_TYPES.SUBMISSION} exportParams={queryParams}>
              <FileDownloadIcon />
            </ApplicationsExport>
          </IconButton>
        </Tooltip>

        {rowsSelected > 0 ? (
          <>
            {/* Bulk actions when items are selected */}
            <Tooltip title={t('common:Assign Reviewers')}>
              <IconButton onClick={handleAssignReviewers} size="medium" color="primary">
                <AssignmentIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('common:Send Email')}>
              <IconButton onClick={handleSendEmail} size="medium" color="primary">
                <MailOutlineIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('common:Send Registration Reminder')}>
              <IconButton onClick={handleSendReminder} size="medium" color="success">
                <NotificationsIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('common:Delete')}>
              <IconButton onClick={handleDelete} size="medium" sx={{ color: 'error.main' }}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            {/* Column visibility */}
            <Tooltip title={t('common:Toggle column visibility')} placement="top">
              <IconButton
                onClick={handleColumnsPopoverOpen}
                size="medium"
                sx={{
                  color: isColumnsPopoverOpen ? 'primary.main' : 'rgba(0, 0, 0, 0.54)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <ViewColumnIcon />
              </IconButton>
            </Tooltip>

            {/* Filter */}
            <Tooltip title={t('common:Filter list')} placement="top">
              <IconButton
                aria-label="filter list"
                onClick={handleFilterPopoverOpen}
                size="medium"
                sx={{
                  color: (filters.status && filters.status.length > 0) || isFilterPopoverOpen ? 'primary.main' : 'rgba(0, 0, 0, 0.54)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* Columns popover */}
      <Popover
        id="columns-popover"
        open={isColumnsPopoverOpen}
        anchorEl={columnsAnchorEl}
        onClose={handleColumnsPopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
            borderRadius: '8px',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            mt: 1,
            width: 250
          }
        }}
      >
        <Paper sx={popoverPaperStyle}>
          <Typography variant="subtitle1" sx={{ mb: 1 }} fontSize="14px">
            {t('common:Select columns')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormGroup>
            {columns
              .filter((column) => column.isDynamic) // Only show dynamic columns in the menu
              .map((column) => (
                <FormControlLabel
                  key={column.id}
                  control={
                    <Checkbox
                      checked={column.isVisible}
                      onChange={() => onToggleColumn(column.id)}
                      name={column.id}
                      size="small"
                    />
                  }
                  label={column.label}
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                />
              ))}
          </FormGroup>
        </Paper>
      </Popover>

      {/* Filter popover */}
      <Popover
        id="filter-popover"
        open={isFilterPopoverOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterPopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            boxShadow: 3,
            borderRadius: 2,
            border: '1px solid rgba(0, 0, 0, 0.05)',
            mt: 1,
            px: 3,
            py: 2,
            minWidth: 400,
            maxWidth: 900,
          }
        }}
      >
        <Paper elevation={0}>
          <Box
            sx={{
              backgroundColor: '#f5f5f5',
              width: '100%',
              px: 3,
              py: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              {t('common:Filter By')}
            </Typography>
          </Box>
          <Box sx={{ px: 3, pt: 2, pb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs='auto'>
                <Typography variant="subtitle1" fontSize="14px" sx={{ mb: 1 }}>
                  {t('common:Status')}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <FormGroup>
                  {statusOptions.map((status) => (
                    <FormControlLabel
                      key={status}
                      control={
                        <Checkbox
                          checked={filters.status?.includes(status) || false}
                          onChange={() => handleStatusFilterChange(status)}
                          name={status}
                          size="small"
                        />
                      }
                      label={formatStatusLabel(status)}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                    />
                  ))}
                </FormGroup>
                {filters.status?.length > 0 && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onFilterChange('status', [])}
                    sx={{ mt: 1, fontSize: '14px' }}
                  >
                    {t('common:Clear filters')}
                  </Button>
                )}
              </Grid>

              <Grid item xs='auto'>
                <Typography variant="subtitle1" fontSize="14px" sx={{ mb: 1 }}>
                  {t('common:Registration Status')}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <FormGroup>
                  {registrationStatusOptions.map((regStatus) => (
                    <FormControlLabel
                      key={regStatus}
                      control={
                        <Checkbox
                          checked={filters.registration_status?.includes(regStatus) || false}
                          onChange={() => handleRegistrationStatusFilterChange(regStatus)}
                          name={regStatus}
                          size="small"
                        />
                      }
                      label={formatStatusLabel(regStatus)}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                    />
                  ))}
                </FormGroup>
                {filters.registration_status?.length > 0 && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onFilterChange('registration_status', [])}
                    sx={{ mt: 1, fontSize: '14px' }}
                  >
                    {t('common:Clear filters')}
                  </Button>
                )}
              </Grid>

              <Grid item xs='auto'>
                <Typography variant="subtitle1" fontSize="14px" sx={{ mb: 1 }}>
                  {t('common:Payment Status')}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <FormGroup>
                  {paymentStatusOptions.map((payStatus) => (
                    <FormControlLabel
                      key={payStatus}
                      control={
                        <Checkbox
                          checked={filters.payment_status?.includes(payStatus) || false}
                          onChange={() => handlePaymentStatusFilterChange(payStatus)}
                          name={payStatus}
                          size="small"
                        />
                      }
                      label={formatStatusLabel(payStatus)}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                    />
                  ))}
                </FormGroup>
                {filters.payment_status?.length > 0 && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onFilterChange('payment_status', [])}
                    sx={{ mt: 1, fontSize: '14px' }}
                  >
                    {t('common:Clear filters')}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Popover>
    </Toolbar>
  );
}

SubmissionsTableToolbar.propTypes = {
  rowsSelected: PropTypes.number.isRequired,
  columns: PropTypes.array.isRequired,
  onToggleColumn: PropTypes.func.isRequired,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  opportunity: PropTypes.object.isRequired,
  onDelete: PropTypes.func,
  onSendReminder: PropTypes.func,
  onSendEmail: PropTypes.func,
  onAssignReviewers: PropTypes.func,
};

SubmissionsTableToolbar.defaultProps = {
  filters: {},
  onFilterChange: () => { },
  onSearchChange: null,
  onDelete: null,
  onSendReminder: null,
  onSendEmail: null,
  onAssignReviewers: null,
};
